import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/time";
import {
  Plus, Copy, ExternalLink, Link2, MousePointerClick,
  UserCheck, Zap, CheckCircle2, XCircle, CalendarClock, Eye, Globe, Activity, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CYCLE_LABEL = { monthly: "Monthly", yearly: "Yearly" };
const MODE_LABEL  = { preview: "Preview Page", direct: "Direct Reg." };

export default function PartnerLinks() {
  const [links,      setLinks]      = useState([]);
  const [plans,      setPlans]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [form,       setForm]       = useState({ plan_id: "", billing_cycle: "monthly", link_type: "prospect", landing_mode: "" });
  const [config,     setConfig]     = useState({ default_expiry_days: 30, default_landing_mode: "preview" });
  const [myType,     setMyType]     = useState("sales_partner"); // current partner's own type
  const [activityLink,    setActivityLink]    = useState(null); // link whose activity is open
  const [activityData,    setActivityData]    = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const MANAGER_PLUS = new Set(["sales_manager", "zonal_partner", "regional_partner", "national_partner"]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linksR, plansR] = await Promise.all([
        axios.get(`${API}/api/sm/referral-links/partner-links`, { withCredentials: true }),
        axios.get(`${API}/api/plans`, { withCredentials: true }),
      ]);
      setLinks(linksR.data.links || []);
      setPlans(plansR.data.plans || plansR.data || []);
      // Config is admin-only; fetch separately and ignore 403
      axios.get(`${API}/api/sm/referral-links/config`, { withCredentials: true })
        .then(cfgR => setConfig(cfgR.data))
        .catch(() => {});
      // Also fetch own partner profile to know our type
      const meR = await axios.get(`${API}/api/sm/partners/portal/me`, { withCredentials: true }).catch(() => null);
      if (meR?.data?.partner_type) setMyType(meR.data.partner_type);
    } catch { toast.error("Failed to load links"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shortUrl = (code) => `${window.location.origin}/join/${code}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied!");
  };

  const shareWhatsApp = (url, l) => {
    const msg = l.link_type === "partner"
      ? `Join the Law OS Partner Network and earn commissions by referring law firms. Apply here: ${url}`
      : `Join me on Law OS — ${l.plan_name} (${CYCLE_LABEL[l.billing_cycle]}). Sign up here: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const loadActivity = async (link) => {
    setActivityLink(link);
    setActivityData([]);
    setActivityLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/sm/referral-links/partner-activity/${link.short_code}`,
        { withCredentials: true }
      );
      setActivityData(r.data.events || []);
    } catch {
      toast.error("Could not load activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleCreate = async () => {
    if (form.link_type === "prospect" && !form.plan_id) { toast.error("Select a plan"); return; }
    setSaving(true);
    try {
      const body = {
        partner_id: "self",
        link_type:  form.link_type,
        ...(form.link_type === "prospect" && { plan_id: form.plan_id, billing_cycle: form.billing_cycle }),
        ...(form.landing_mode && { landing_mode: form.landing_mode }),
      };
      await axios.post(`${API}/api/sm/referral-links/partner-create`, body, { withCredentials: true });
      toast.success("Referral link generated!");
      setShowCreate(false);
      setForm({ plan_id: "", billing_cycle: "monthly", link_type: "prospect", landing_mode: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create link"); }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50";

  const totalClicks = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalRegs   = links.reduce((s, l) => s + (l.registrations || 0), 0);
  const totalActs   = links.reduce((s, l) => s + (l.activations || 0), 0);

  return (
    <div className="space-y-6 p-4" data-testid="partner-links-page">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-foreground font-light">My Referral Links</h1>
          <p className="text-sm text-gray-500 mt-1">Generate links for specific plans — monthly or yearly</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="partner-create-link-btn"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 transition-all shrink-0">
          <Plus className="w-4 h-4" />Generate Link
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: MousePointerClick, label: "Total Clicks",  value: totalClicks, color: "text-blue-400" },
          { icon: UserCheck,         label: "Registrations", value: totalRegs,   color: "text-amber-400" },
          { icon: Zap,               label: "Activations",   value: totalActs,   color: "text-emerald-400" },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <Icon className={`w-4 h-4 ${color} mb-2`} />
            <p className="text-xl font-bold text-foreground">{value}</p>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Links list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}
        </div>
      ) : links.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mb-3">
            <Link2 className="w-5 h-5 text-gray-500" />
          </div>
          <p className="text-[13px] font-medium text-foreground">No referral links yet</p>
          <p className="text-[11px] text-gray-500 mt-1">Generate your first link to start earning commissions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map(l => {
            const isExpired = l.expires_at && new Date(l.expires_at) < new Date();
            const url = shortUrl(l.short_code);
            return (
              <div key={l.id} data-testid={`link-card-${l.id}`}
                className={`bg-white/5 border rounded-xl overflow-hidden transition-all ${
                  l.is_active && !isExpired ? "border-white/10" : "border-white/5 opacity-60"
                }`}>

                {/* Top row */}
                <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-white/5">
                  <div className="flex items-center gap-2 min-w-0">
                    <code className="text-[12px] font-mono text-primary bg-primary/10 px-2 py-0.5 rounded shrink-0">{l.short_code}</code>
                    {l.link_type === "partner" ? (
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold shrink-0 bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Refer a Partner
                      </span>
                    ) : (
                      <>
                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold shrink-0 ${
                          l.billing_cycle === "yearly"
                            ? "bg-violet-500/10 text-violet-400 border border-violet-500/20"
                            : "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                        }`}>{CYCLE_LABEL[l.billing_cycle]}</span>
                        <span className="text-[12px] text-foreground font-medium truncate">{l.plan_name}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {l.is_active && !isExpired
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                      : <XCircle className="w-3.5 h-3.5 text-gray-500" />
                    }
                    <span className={`text-[10px] ${l.is_active && !isExpired ? "text-emerald-400" : "text-gray-500"}`}>
                      {isExpired ? "Expired" : l.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                {/* URL + actions */}
                <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-white/5">
                  <span className="text-[11px] text-gray-400 truncate flex-1 min-w-0">{url}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => copy(url)}
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-white/10 text-gray-400 rounded-lg hover:bg-white/5 transition-all">
                      <Copy className="w-3 h-3" />Copy
                    </button>
                    <button onClick={() => shareWhatsApp(url, l)}
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-green-500/20 text-green-400 rounded-lg hover:bg-green-500/5 transition-all">
                      Share
                    </button>
                    <button onClick={() => loadActivity(l)}
                      data-testid={`activity-btn-${l.short_code}`}
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-primary/20 text-primary rounded-lg hover:bg-primary/5 transition-all">
                      <Activity className="w-3 h-3" />Activity
                    </button>
                    <a href={url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 border border-white/10 text-gray-400 rounded-lg hover:bg-white/5 transition-all">
                      <ExternalLink className="w-3 h-3" />Preview
                    </a>
                  </div>
                </div>

                {/* Stats row */}
                <div className="px-4 py-2.5 flex flex-wrap items-center gap-4 text-[11px]">
                  <span className="flex items-center gap-1 text-gray-400">
                    <MousePointerClick className="w-3 h-3" />{l.clicks || 0} clicks
                  </span>
                  <span className="flex items-center gap-1 text-amber-400">
                    <UserCheck className="w-3 h-3" />{l.registrations || 0} registrations
                  </span>
                  <span className="flex items-center gap-1 text-emerald-400">
                    <Zap className="w-3 h-3" />{l.activations || 0} activations
                  </span>
                  <span className="flex items-center gap-1 text-gray-500 ml-auto">
                    <CalendarClock className="w-3 h-3" />
                    {l.expires_at ? `Expires ${fmtDate(l.expires_at)}` : "No expiry"}
                  </span>
                  <span className="flex items-center gap-1 text-gray-500">
                    {l.landing_mode === "preview" ? <Eye className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                    {MODE_LABEL[l.landing_mode]}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Activity modal */}
      {activityLink && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />Link Activity
                </h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  <code className="text-primary font-mono">{activityLink.short_code}</code>
                  {activityLink.link_type !== "partner" && activityLink.plan_name && ` · ${activityLink.plan_name}`}
                </p>
              </div>
              <button onClick={() => setActivityLink(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground text-lg leading-none">✕</button>
            </div>
            {/* Stats row */}
            <div className="flex gap-5 px-6 py-3 bg-muted/20 border-b border-border text-[11px]">
              <span className="flex items-center gap-1.5"><MousePointerClick className="w-3 h-3 text-blue-500" /><span className="text-muted-foreground">Clicks:</span><span className="font-semibold">{activityLink.clicks || 0}</span></span>
              <span className="flex items-center gap-1.5"><UserCheck className="w-3 h-3 text-amber-500" /><span className="text-muted-foreground">Registered:</span><span className="font-semibold">{activityLink.registrations || 0}</span></span>
              <span className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-emerald-500" /><span className="text-muted-foreground">Activated:</span><span className="font-semibold">{activityLink.activations || 0}</span></span>
            </div>
            {/* Events list */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {activityLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : activityData.length === 0 ? (
                <div className="text-center py-10">
                  <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-30" />
                  <p className="text-sm text-muted-foreground">No activity recorded yet</p>
                  <p className="text-[11px] text-muted-foreground/70 mt-1">Share this link to start tracking engagement</p>
                </div>
              ) : (
                activityData.map((ev, i) => <ActivityRow key={ev.id || i} event={ev} />)
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" data-testid="partner-create-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Generate Referral Link</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Link type selector with access control */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Referral Type</label>
                <div className="flex gap-2">
                  <button onClick={() => setForm({...form, link_type: "prospect", plan_id: "", billing_cycle: "monthly"})}
                    className={`flex-1 py-2.5 px-3 rounded-xl border text-left transition-all ${
                      form.link_type === "prospect" ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                    }`}>
                    <p className="text-[12px] font-semibold">Refer a Prospect</p>
                    <p className="text-[10px] opacity-70 mt-0.5">Subscription page</p>
                  </button>
                  {MANAGER_PLUS.has(myType) && (
                    <button onClick={() => setForm({...form, link_type: "partner", plan_id: ""})}
                      className={`flex-1 py-2.5 px-3 rounded-xl border text-left transition-all ${
                        form.link_type === "partner" ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      <p className="text-[12px] font-semibold">Refer a Partner</p>
                      <p className="text-[10px] opacity-70 mt-0.5">Become partner page</p>
                    </button>
                  )}
                </div>
              </div>

              {/* Plan + Cycle only for prospect */}
              {form.link_type === "prospect" && (
                <>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Plan</label>
                    <select value={form.plan_id} onChange={e => setForm({...form, plan_id: e.target.value})} className={inp} data-testid="partner-select-plan">
                      <option value="">Select plan…</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Billing Cycle</label>
                    <div className="flex gap-2">
                      {["monthly", "yearly"].map(c => (
                        <button key={c} onClick={() => setForm({...form, billing_cycle: c})}
                          className={`flex-1 py-2 rounded-lg border text-[12px] font-medium capitalize transition-all ${
                            form.billing_cycle === c ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                          }`}>{c}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Landing Mode</label>
                <select value={form.landing_mode} onChange={e => setForm({...form, landing_mode: e.target.value})} className={inp}>
                  <option value="">Default ({MODE_LABEL[config.default_landing_mode]})</option>
                  <option value="preview">Preview Page</option>
                  <option value="direct">Direct</option>
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground">Link expires after {config.default_expiry_days} days (global default).</p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">Cancel</button>
              <button onClick={handleCreate} disabled={saving} data-testid="partner-confirm-create"
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
                {saving ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Activity event row component ─────────────────────────────────────────────
function ActivityRow({ event }) {
  const cfg = {
    click:        { label: "Click",      color: "bg-blue-500/10 text-blue-500 border-blue-500/20",     dot: "bg-blue-500" },
    registration: { label: "Registered", color: "bg-amber-500/10 text-amber-500 border-amber-500/20",  dot: "bg-amber-500" },
    activation:   { label: "Activated",  color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20", dot: "bg-emerald-500" },
  }[event.event_type] || { label: event.event_type, color: "bg-muted text-muted-foreground", dot: "bg-muted" };

  const time = event.created_at
    ? fmtDateTime(event.created_at)
    : "—";

  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${cfg.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${cfg.color}`}>
            {cfg.label}
          </span>
          <span className="text-[11px] text-muted-foreground">{time}</span>
        </div>
        {event.event_type === "click" && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {event.browser || "Browser"} · IP: {event.ip_masked || "—"}
          </p>
        )}
        {(event.event_type === "registration" || event.event_type === "activation") && (
          <p className="text-[12px] mt-0.5">
            {event.name && <span className="font-medium text-foreground">{event.name}</span>}
            {event.email && <span className="text-muted-foreground"> · {event.email}</span>}
          </p>
        )}
      </div>
    </div>
  );
}
